import type {ReactElement} from 'react';
import React, {useState, useRef, useEffect} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {Tooltip} from '@Components/tooltip';
import {Text, TextSize} from '@Components/text';
import {setHoveredItemId, startItemModifying, updateItemModifying, endItemModifying} from '../../timeline-reducer';
import {isEditorMobileVariant} from '@Components/poster-editor/library/is-editor-mobile-variant';
import {useTimelineScrollContainerRef} from '../../hooks/useTimelineRefs';
import {useTimelineWidthPerSecond, useTimelineSeekToClientX} from '../../hooks/timeline-hooks';
import {ResizeHandle} from '../resize-handle/resize-handle';
import {getItemLeft, getItemWidth, calculateDraggedTimes} from '../../utils/timeline-calculations';
import {formatSecondsToTimeString} from '@Utils/date.util';
import {PAGE_TRACK_HEIGHT, TRACK_GAP, TIMELINE_ITEM_DATA_ATTRIBUTE, TimelineItemType, MIN_ITEM_DURATION, PAGE_ITEM_GAP} from '../../timeline.types';
import type {TimelineItemProps, ResizeCalculationParams} from './timeline-item.types';
import styles from './timeline-item.module.scss';

export function TimelineItem({
  id,
  startTime,
  endTime,
  minDuration: _minDuration = MIN_ITEM_DURATION,
  subRow,
  variant = TimelineItemType.AUDIO,
  label,
  isSelected = false,
  trackHeight = PAGE_TRACK_HEIGHT,
  children,
  onClick,
  onDragStart,
  onDragMove,
  onDragEnd,
  onResizeMove,
  onResizeEnd,
  onCalculateResize,
}: TimelineItemProps): ReactElement {
  const dispatch = useAppDispatch();
  const pixelsPerSecond = useTimelineWidthPerSecond();
  const scrollContainerRef = useTimelineScrollContainerRef();

  const seekToClientX = useTimelineSeekToClientX();
  const isHovered = useAppSelector((state) => state.timeline.hoveredItemId === id);
  const itemModifying = useAppSelector((state) => state.timeline.itemModifying);
  const isThisItemModifying = itemModifying?.itemId === id;
  const isDragging = isThisItemModifying && itemModifying?.action.type === 'dragging';
  const isResizing = isThisItemModifying && itemModifying?.action.type === 'resizing';

  // Use Redux-tracked times during modification so the item moves in real time
  const currentStartTime = isThisItemModifying ? itemModifying.startTime : startTime;
  const currentEndTime = isThisItemModifying ? itemModifying.endTime : endTime;

  const [tooltipPosition, setTooltipPosition] = useState<{left: number; top: number}>({left: 0, top: 0});

  // DOM refs for resize handle tooltip positioning
  const leftHandleRef = useRef<HTMLDivElement>(null);
  const rightHandleRef = useRef<HTMLDivElement>(null);

  // Active window listeners — stored so they can be removed on pointerup or unmount
  const activeListenersRef = useRef<{move: EventListener | null; up: EventListener | null}>({move: null, up: null});

  // Drag state
  const dragStartXRef = useRef<number>(0);
  const hasDragMoved = useRef<boolean>(false);
  const isDragReadyRef = useRef<boolean>(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressNextClickRef = useRef<boolean>(false);

  // Resize state
  const resizeHandleRef = useRef<'left' | 'right'>('right');

  // Captured at interaction start for delta calculations
  const originalStartTime = useRef<number>(startTime);
  const originalEndTime = useRef<number>(endTime);

  // Callback refs — kept current to avoid stale closures in window listeners
  const onDragStartRef = useRef(onDragStart);
  onDragStartRef.current = onDragStart;
  const onDragMoveRef = useRef(onDragMove);
  onDragMoveRef.current = onDragMove;
  const onDragEndRef = useRef(onDragEnd);
  onDragEndRef.current = onDragEnd;
  const onResizeMoveRef = useRef(onResizeMove);
  onResizeMoveRef.current = onResizeMove;
  const onResizeEndRef = useRef(onResizeEnd);
  onResizeEndRef.current = onResizeEnd;
  const onCalculateResizeRef = useRef(onCalculateResize);
  onCalculateResizeRef.current = onCalculateResize;

  // Live value refs — kept current so window listeners always read latest values
  const pixelsPerSecondRef = useRef(pixelsPerSecond);
  pixelsPerSecondRef.current = pixelsPerSecond;
  const itemModifyingRef = useRef(itemModifying);
  itemModifyingRef.current = itemModifying;
  const startTimeRef = useRef(startTime);
  startTimeRef.current = startTime;
  const endTimeRef = useRef(endTime);
  endTimeRef.current = endTime;

  // Clean up any lingering listeners if the component unmounts during a drag/resize
  useEffect(() => {
    return (): void => {
      const {move, up} = activeListenersRef.current;
      if (move) window.removeEventListener('pointermove', move);
      if (up) window.removeEventListener('pointerup', up);
      document.body.classList.remove('timeline-dragging');
      if (longPressTimerRef.current !== null) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  const addPointerListeners = (move: EventListener, up: EventListener): void => {
    activeListenersRef.current = {move, up};
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const removePointerListeners = (): void => {
    const {move, up} = activeListenersRef.current;
    if (move) window.removeEventListener('pointermove', move);
    if (up) window.removeEventListener('pointerup', up);
    activeListenersRef.current = {move: null, up: null};
  };

  const getPointerTime = (clientX: number): number => {
    if (!scrollContainerRef?.current) {
      return 0;
    }
    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    const pointerX = clientX - containerRect.left + scrollContainerRef.current.scrollLeft;
    return Math.max(0, pointerX / pixelsPerSecondRef.current);
  };

  // --- Drag ---

  const handleDragMove = (e: PointerEvent): void => {
    if (isEditorMobileVariant() && !isDragReadyRef.current) {
      // Cancel long-press if finger moved too far; native scroll handles panning
      if (Math.abs(e.clientX - dragStartXRef.current) > 8 && longPressTimerRef.current !== null) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      return;
    }

    hasDragMoved.current = true;
    const deltaX = e.clientX - dragStartXRef.current;
    const {startTime: newStart, endTime: newEnd} = calculateDraggedTimes(originalStartTime.current, originalEndTime.current, deltaX, pixelsPerSecondRef.current);

    dispatch(updateItemModifying({startTime: newStart, endTime: newEnd}));
    onDragMoveRef.current?.(id, {startTime: newStart, endTime: newEnd, cursorTime: getPointerTime(e.clientX)});
  };

  const handleDragUp = async (e: PointerEvent): Promise<void> => {
    if (longPressTimerRef.current !== null) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    document.body.classList.remove('timeline-dragging');
    removePointerListeners();

    const wasDragReady = isDragReadyRef.current;
    isDragReadyRef.current = false;

    if (wasDragReady && hasDragMoved.current) {
      suppressNextClickRef.current = true;
    }

    const finalStartTime = itemModifyingRef.current?.startTime ?? startTimeRef.current;
    const finalEndTime = itemModifyingRef.current?.endTime ?? endTimeRef.current;

    try {
      if (wasDragReady && hasDragMoved.current && onDragEndRef.current) {
        await onDragEndRef.current(id, {startTime: finalStartTime, endTime: finalEndTime, cursorTime: getPointerTime(e.clientX)});
      }
    } finally {
      dispatch(endItemModifying());
    }
  };

  const handleDragStart = (e: React.PointerEvent): void => {
    const isMobile = isEditorMobileVariant();

    e.stopPropagation();
    if (!isMobile) {
      e.preventDefault();
    }

    dragStartXRef.current = e.clientX;
    hasDragMoved.current = false;
    originalStartTime.current = startTime;
    originalEndTime.current = endTime;
    isDragReadyRef.current = !isMobile;

    if (!isMobile) {
      addPointerListeners(handleDragMove as unknown as EventListener, handleDragUp as unknown as EventListener);
      suppressNextClickRef.current = true;
      seekToClientX(e.clientX);
      onClick?.(id);
      document.body.classList.add('timeline-dragging');
      dispatch(startItemModifying({itemId: id, action: {type: 'dragging'}, startTime, endTime, timelineItemType: variant}));
      onDragStartRef.current?.(id);
    } else if (isSelected) {
      // Selected items have touch-action: none, so we get pointer events for drag
      addPointerListeners(handleDragMove as unknown as EventListener, handleDragUp as unknown as EventListener);
      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null;
        isDragReadyRef.current = true;
        navigator.vibrate?.(50);
        document.body.classList.add('timeline-dragging');
        dispatch(startItemModifying({itemId: id, action: {type: 'dragging'}, startTime: startTimeRef.current, endTime: endTimeRef.current, timelineItemType: variant}));
        onDragStartRef.current?.(id);
      }, 400);
    }
  };

  // --- Resize ---

  const updateTooltipPosition = (): void => {
    const handleRef = resizeHandleRef.current === 'left' ? leftHandleRef : rightHandleRef;
    if (!handleRef.current) {
      return;
    }
    const rect = handleRef.current.getBoundingClientRect();
    setTooltipPosition({left: rect.left, top: rect.top});
  };

  const handleResizeMove = (e: PointerEvent): void => {
    const deltaX = e.clientX - dragStartXRef.current;
    const params: ResizeCalculationParams = {
      originalStartTime: originalStartTime.current,
      originalEndTime: originalEndTime.current,
      deltaPixels: deltaX,
      handle: resizeHandleRef.current,
      pixelsPerSecond: pixelsPerSecondRef.current,
    };
    const {startTime: newStart, endTime: newEnd} = onCalculateResizeRef.current(params);

    dispatch(updateItemModifying({startTime: newStart, endTime: newEnd}));
    updateTooltipPosition();
    onResizeMoveRef.current?.(id, {startTime: newStart, endTime: newEnd, handle: resizeHandleRef.current});
  };

  const handleResizeUp = async (): Promise<void> => {
    removePointerListeners();

    const finalStartTime = itemModifyingRef.current?.startTime ?? startTimeRef.current;
    const finalEndTime = itemModifyingRef.current?.endTime ?? endTimeRef.current;

    try {
      if (onResizeEndRef.current) {
        await onResizeEndRef.current(id, {startTime: finalStartTime, endTime: finalEndTime, handle: resizeHandleRef.current});
      }
    } finally {
      dispatch(endItemModifying());
    }
  };

  const handleResizeStart = (e: React.PointerEvent, handle: 'left' | 'right'): void => {
    e.preventDefault();
    e.stopPropagation();

    if (!isEditorMobileVariant()) {
      seekToClientX(e.clientX);
      onClick?.(id);
    }

    resizeHandleRef.current = handle;
    dragStartXRef.current = e.clientX;
    originalStartTime.current = startTime;
    originalEndTime.current = endTime;

    dispatch(startItemModifying({itemId: id, action: {type: 'resizing', resizeHandle: handle}, startTime, endTime, timelineItemType: variant}));
    setTimeout(updateTooltipPosition, 0);
    addPointerListeners(handleResizeMove as unknown as EventListener, handleResizeUp as unknown as EventListener);
  };

  // --- Render ---

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }
    if (!isDragging && !isResizing) {
      if (!isEditorMobileVariant()) {
        seekToClientX(e.clientX);
      }
      onClick?.(id);
    }
  };

  const width = getItemWidth(currentStartTime, currentEndTime, pixelsPerSecond);
  const left = getItemLeft(currentStartTime, pixelsPerSecond);
  const top = subRow * (trackHeight + TRACK_GAP);
  const duration = currentEndTime - currentStartTime;

  const displayWidth = variant === TimelineItemType.PAGE ? width - PAGE_ITEM_GAP : width;

  const originalWidth = getItemWidth(startTime, endTime, pixelsPerSecond);
  const originalLeft = getItemLeft(startTime, pixelsPerSecond);
  const placeholderWidth = variant === TimelineItemType.PAGE ? originalWidth - PAGE_ITEM_GAP : originalWidth;

  // GPU-accelerated drag: keep the element at its original DOM position and
  // use transform so the browser can composite it without a layout pass.
  const translateX = isDragging ? left - originalLeft : 0;

  const showHandles = (!isEditorMobileVariant() && isHovered) || isSelected || isDragging || isResizing;

  const itemClassName = [styles.item, styles[variant], isSelected && styles.selected, isDragging && styles.dragging, isResizing && styles.resizing].filter(Boolean).join(' ');

  return (
    <>
      {isResizing ? (
        <Tooltip
          style={{left: tooltipPosition.left - 16, top: tooltipPosition.top - 52, position: 'fixed'}}
          className={styles.tooltip}
          id={`resize-tooltip-${id}`}
          text={<Text size={TextSize.XSMALL} val={formatSecondsToTimeString(duration, {showDecimals: true})} />}
        />
      ) : null}

      {isDragging ? (
        <div className={styles.placeholder} style={{width: `${placeholderWidth}px`, left: `${originalLeft}px`, top: `${top}px`, height: `${trackHeight}px`}} aria-hidden="true" />
      ) : null}

      <div
        className={itemClassName}
        style={{
          width: `${displayWidth}px`,
          left: isDragging ? `${originalLeft}px` : `${left}px`,
          top: `${top}px`,
          height: `${trackHeight}px`,
          transform: isDragging ? `translate(${translateX}px, 0px)` : 'none',
          willChange: isDragging ? 'transform' : 'auto',
        }}
        {...{[TIMELINE_ITEM_DATA_ATTRIBUTE]: id}}
        onPointerDown={handleDragStart}
        onClick={handleClick}
        onMouseEnter={(): void => {
          dispatch(setHoveredItemId(id));
        }}
        onMouseLeave={(): void => {
          dispatch(setHoveredItemId(undefined));
        }}
        role="button"
        tabIndex={0}
        aria-label={label ?? `Timeline item ${id}`}
      >
        <ResizeHandle
          ref={leftHandleRef}
          side="left"
          onPointerDown={(e): void => {
            handleResizeStart(e, 'left');
          }}
          isVisible={showHandles}
          isItemSelected={isSelected}
        />
        <div className={styles.content}>{children}</div>
        <ResizeHandle
          ref={rightHandleRef}
          side="right"
          onPointerDown={(e): void => {
            handleResizeStart(e, 'right');
          }}
          isVisible={showHandles}
          isItemSelected={isSelected}
        />
      </div>
    </>
  );
}
